import SquareIcon from "@mui/icons-material/Square";
import { Autocomplete, ListItem, Radio, TextField } from "@mui/material";
import lodash from "lodash";
import { Dispatch, SetStateAction } from "react";
import { Option } from "../constants/common";
import { useInfiniteScroll } from "../hooks/use-infinite-scroll";
import { INPUT_FIELD_HEIGHT } from "../theme";
import { DGT_Pagination } from "../utils/utils";
import { CustomSelectProps, Value } from "./CustomSelect";
import ErrorHelper from "./ErrorWrapper";
import Flex from "./Flex";
import { inputLabel } from "./MaskInput";
import { useFieldLock } from "./ReadOnlyFields";
import { PRIMITIVE } from "./ds";

interface CustomSelectScrollProps extends Omit<CustomSelectProps, "onChange"> {
  // Also receives the picked option so callers don't re-find it in the current
  // page — the full list isn't available once scroll has loaded later pages.
  onChange?: (value: Value, option?: Option | null) => void;
  loading?: boolean;
  pagination: DGT_Pagination;
  setPagination: Dispatch<SetStateAction<DGT_Pagination>>;
  totalElements?: number;
}

/**
 * Infinite-scroll variant of {@link CustomSelect}: same string-value API, but
 * the caller feeds each page's `options` plus `pagination`/`totalElements` and
 * the listbox accumulates pages and requests the next one on scroll.
 */
export const CustomSelectScroll: React.FC<CustomSelectScrollProps> = ({
  name,
  label,
  flexProps,
  placeholder = "",
  options,
  onChange,
  value,
  optionWithCheckbox = false,
  disableCloseOnSelect = false,
  clearable = true,
  endIcon,
  required,
  error,
  onSearch,
  withStatusBox = false,
  readOnly = false,
  disabled = false,
  handleBlur,
  fullHeight,
  gutterTop,
  enableFilterOption,
  renderCustomLabel,
  disableSortOptions,
  disablePortal = false,
  loading = false,
  pagination,
  setPagination,
  totalElements,
}) => {
  const lock = useFieldLock(readOnly || disabled);

  const { items, onScroll } = useInfiniteScroll<Option>({
    pagination,
    setPagination,
    content: options,
    totalElements,
    isFetching: loading,
    dedupeKey: "value",
  });

  const handleInputChange = (e: any, newInputValue: string, reason: string) => {
    if (reason === "input") {
      onSearch?.(e, newInputValue, reason);
    }
  };

  return (
    <form autoComplete="off">
      <Flex
        {...flexProps}
        flexDirection={flexProps?.flexDirection ?? "column"}
        height={"100%"}
      >
        <ErrorHelper error={error}>
          <Autocomplete
            disablePortal={disablePortal}
            style={{
              height: fullHeight ? "100%" : INPUT_FIELD_HEIGHT,
              marginTop: gutterTop ? "30px" : undefined,
            }}
            sx={
              fullHeight
                ? {
                    "& .MuiOutlinedInput-root": { height: "100%" },
                    "& .MuiInputBase-input": { height: "100% !important" },
                  }
                : undefined
            }
            onBlur={handleBlur}
            disabled={lock.disabled}
            readOnly={lock.readOnly}
            forcePopupIcon={lock.readOnly ? false : undefined}
            ListboxProps={{
              onScroll: (e: React.UIEvent<HTMLElement>) =>
                onScroll(e.currentTarget),
            }}
            options={
              (disableSortOptions
                ? items.filter((a) => a?.value !== value)
                : [...items].sort((a, b) => a?.label?.localeCompare(b?.label))
              ).filter((a) => a?.value !== value) ?? []
            }
            fullWidth
            disableClearable={!clearable}
            disableCloseOnSelect={disableCloseOnSelect}
            getOptionLabel={(option) => option.label}
            popupIcon={endIcon}
            onInputChange={handleInputChange}
            isOptionEqualToValue={lodash.isEqual}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.preventDefault();
            }}
            filterOptions={enableFilterOption ? (opt) => opt : undefined}
            value={items.find((opt) => opt?.value === value) ?? null}
            onChange={(_, val) => onChange?.(val?.value ?? null, val)}
            renderOption={(props, option) => (
              <ListItem
                key={option.value}
                {...props}
                sx={{ fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px` }}
              >
                {optionWithCheckbox && (
                  <Radio
                    checked={option.value === value}
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                )}

                {withStatusBox && (
                  <SquareIcon
                    sx={{
                      backgroundColor: option.color,
                      color: option.color,
                      marginRight: "5px",
                    }}
                  />
                )}

                {renderCustomLabel ? renderCustomLabel(option) : option.label}
              </ListItem>
            )}
            renderInput={(params: any) => (
              <TextField
                label={inputLabel(label, required)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.preventDefault();
                }}
                name={name}
                sx={{
                  ...params?.sx,
                  fontSize: `${PRIMITIVE.typography.fontSize.body.b5}px`,
                }}
                {...params}
                InputLabelProps={{ shrink: true }}
                error={!!error}
                placeholder={placeholder}
              />
            )}
          />
        </ErrorHelper>
      </Flex>
    </form>
  );
};

export default CustomSelectScroll;
